/**
 * Production-Ready Logger for KnightSteps Frontend
 * 
 * Features:
 * - Environment-aware logging (suppresses verbose debug/info in production)
 * - Dynamic production debug toggle via localStorage.getItem('DEBUG_LOGS') === 'true'
 * - Sensitive data redaction (passwords, tokens, jwt, secrets, auth headers)
 * - Styled console badges in development
 * - Global console interception for 700+ legacy console calls
 * - Global window error & unhandled promise rejection handlers
 */

const LOG_LEVELS = {
  error: 0,
  warn: 1,
  info: 2,
  debug: 3
};

// Sensitive field patterns to redact automatically
const SENSITIVE_KEY_PATTERNS = [
  /pass(word)?/i,
  /hash/i,
  /secret/i,
  /token/i,
  /jwt/i,
  /auth(orization)?/i,
  /bearer/i,
  /cookie/i,
  /key/i,
  /signature/i,
  /credit[-_]?card/i,
  /cvv/i,
  /card[-_]?number/i,
  /otp/i,
  /passcode/i
];

/**
 * Deeply sanitizes an object/array, redacting sensitive keys and circular structures
 */
export function redactSensitiveData(data, seen = new WeakSet()) {
  if (data === null || data === undefined) return data;

  if (typeof data !== 'object') {
    return data;
  }

  if (data instanceof Date || data instanceof RegExp) {
    return data;
  }

  if (data instanceof Error) {
    return {
      name: data.name,
      message: data.message,
      stack: data.stack
    };
  }

  if (seen.has(data)) {
    return '[Circular]';
  }
  seen.add(data);

  if (Array.isArray(data)) {
    return data.map(item => redactSensitiveData(item, seen));
  }

  const sanitized = {};
  for (const [key, value] of Object.entries(data)) {
    const isSensitive = SENSITIVE_KEY_PATTERNS.some(pattern => pattern.test(key));
    if (isSensitive) {
      sanitized[key] = '[REDACTED]';
    } else if (typeof value === 'object' && value !== null) {
      sanitized[key] = redactSensitiveData(value, seen);
    } else if (typeof value === 'string' && value.length > 200 && /^[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+$/.test(value)) {
      sanitized[key] = '[REDACTED_JWT]';
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized;
}

class FrontendLogger {
  constructor(defaultMeta = {}) {
    this.defaultMeta = defaultMeta;
    this.isProduction = process.env.NODE_ENV === 'production';
  }

  isDebugEnabled() {
    if (!this.isProduction) return true;
    try {
      return typeof window !== 'undefined' && window.localStorage && window.localStorage.getItem('DEBUG_LOGS') === 'true';
    } catch {
      return false;
    }
  }

  shouldLog(level) {
    if (level === 'error' || level === 'warn') {
      return true;
    }
    return this.isDebugEnabled();
  }

  child(meta = {}) {
    return new FrontendLogger({
      ...this.defaultMeta,
      ...meta
    });
  }

  withContext(context) {
    return this.child({ context });
  }

  formatArgs(level, message, args) {
    const context = this.defaultMeta.context ? `[${this.defaultMeta.context}]` : '';
    const tag = `[KnightSteps]${context}`;
    const sanitizedArgs = args.map(arg => redactSensitiveData(arg));
    return { tag, message, sanitizedArgs };
  }

  error(message, ...args) {
    if (!this.shouldLog('error')) return;
    const { tag, sanitizedArgs } = this.formatArgs('error', message, args);
    if (typeof console !== 'undefined' && console.error) {
      console.error(`%c${tag} [ERROR]`, 'color: #ef4444; font-weight: bold;', message, ...sanitizedArgs);
    }
  }

  warn(message, ...args) {
    if (!this.shouldLog('warn')) return;
    const { tag, sanitizedArgs } = this.formatArgs('warn', message, args);
    if (typeof console !== 'undefined' && console.warn) {
      console.warn(`%c${tag} [WARN]`, 'color: #f59e0b; font-weight: bold;', message, ...sanitizedArgs);
    }
  }

  info(message, ...args) {
    if (!this.shouldLog('info')) return;
    const { tag, sanitizedArgs } = this.formatArgs('info', message, args);
    if (typeof console !== 'undefined' && console.info) {
      console.info(`%c${tag} [INFO]`, 'color: #3b82f6; font-weight: bold;', message, ...sanitizedArgs);
    }
  }

  debug(message, ...args) {
    if (!this.shouldLog('debug')) return;
    const { tag, sanitizedArgs } = this.formatArgs('debug', message, args);
    if (typeof console !== 'undefined' && (console.debug || console.log)) {
      const fn = console.debug || console.log;
      fn(`%c${tag} [DEBUG]`, 'color: #6b7280; font-weight: bold;', message, ...sanitizedArgs);
    }
  }

  /**
   * Initializes global console interception to sanitize legacy console.log calls in production
   */
  initGlobalConsole() {
    if (typeof window === 'undefined' || this._consoleIntercepted) return;
    this._consoleIntercepted = true;

    const originalConsole = {
      log: console.log.bind(console),
      info: console.info.bind(console),
      warn: console.warn.bind(console),
      error: console.error.bind(console),
      debug: (console.debug || console.log).bind(console)
    };

    this.originalConsole = originalConsole;

    console.error = (msg, ...args) => {
      originalConsole.error(msg, ...args.map(a => redactSensitiveData(a)));
    };

    console.warn = (msg, ...args) => {
      originalConsole.warn(msg, ...args.map(a => redactSensitiveData(a)));
    };

    console.info = (msg, ...args) => {
      if (this.shouldLog('info')) {
        originalConsole.info(msg, ...args.map(a => redactSensitiveData(a)));
      }
    };

    console.debug = (msg, ...args) => {
      if (this.shouldLog('debug')) {
        originalConsole.debug(msg, ...args.map(a => redactSensitiveData(a)));
      }
    };

    console.log = (msg, ...args) => {
      if (this.shouldLog('info')) {
        originalConsole.log(msg, ...args.map(a => redactSensitiveData(a)));
      }
    };
  }

  /**
   * Listens for unhandled browser runtime errors and unhandled promise rejections
   */
  initGlobalErrorHandlers() {
    if (typeof window === 'undefined' || this._handlersAttached) return;
    this._handlersAttached = true;

    window.addEventListener('error', (event) => {
      this.error('Global Unhandled Browser Error', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error
      });
    });

    window.addEventListener('unhandledrejection', (event) => {
      this.error('Global Unhandled Promise Rejection', {
        reason: event.reason instanceof Error ? event.reason.message : event.reason,
        stack: event.reason instanceof Error ? event.reason.stack : undefined
      });
    });
  }
}

const logger = new FrontendLogger();

export default logger;
